import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, sectionType, Thumb, type ThumbSpec } from '@/design-system';
import type { PinnedPreset } from './api';
import { useStructureLabels } from './labels';
import { primary, type Sec } from './useStructure';
import s from './structure.module.css';

/** The wireframe a pinned design draws; a section without one shows the custom block under the AI veil (design: thOf(null)). */
export function specOf(p: Pick<PinnedPreset, 'layout' | 'color_mode' | 'mirrored'> | null): ThumbSpec {
  return p ? { layout: p.layout, mode: p.color_mode, mirrored: p.mirrored } : { layout: 'custom', mode: 'light' };
}

/** Enter or Space on a div that acts as a button. */
export function activate(e: KeyboardEvent, run: () => void) {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  run();
}

type Props = {
  secs: Sec[];
  selected?: number;
  /** the selected section gets the orange outline (List mode only) */
  outline?: boolean;
  /** List mode's try-on: this design replaces the selected section, labelled «Примерка» */
  tryOn?: PinnedPreset | null;
  onSelect?: (i: number) => void;
};

/**
 * «ПРЕДПРОСМОТР СТРАНИЦЫ»: the page as a stack of wireframe sections at their preview ratios. A
 * section without a design shows a dashed «AI подберёт» chip; clicking a section selects it.
 */
export function PageStack({ secs, selected = -1, outline = false, tryOn = null, onSelect }: Props) {
  const { t } = useTranslation('structure');
  const { section } = useStructureLabels();
  return (
    <div className={s.stack} role="list" aria-label={t('preview.label')}>
      {secs.length === 0 && <div className={s.stackEmpty}>{t('preview.empty')}</div>}
      {secs.map((x, i) => {
        const trying = !!tryOn && i === selected;
        const p = trying ? tryOn : primary(x);
        const def = sectionType(x.section_type);
        const name = section(x.section_type);
        return (
          <div key={x.key} role="listitem">
            {/* A div, not a <button>: Chrome paints text inside form controls without subpixel AA, the design's divs with it. */}
            <div
              role="button"
              tabIndex={0}
              className={s.stackSec}
              title={name}
              aria-label={name}
              aria-pressed={i === selected}
              data-outlined={outline && i === selected}
              onClick={() => onSelect?.(i)}
              onKeyDown={(e) => activate(e, () => onSelect?.(i))}
            >
              <Thumb {...specOf(p)} ratio={def.previewRatio} />
              {!p && (
                <span className={s.stackAi}>
                  <span className={s.aiChip}>
                    <Icon name="sparkles" size={10} />
                    {t('preview.ai')}
                  </span>
                </span>
              )}
              <span className={s.stackBorder} />
              {trying && <span className={s.tryOn}>{t('preview.try_on')}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
